import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Camera } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { FloatingNotices } from '../components/FloatingNotices';
import { fetchJsonCached, getCachedJson, setCachedJson } from '../utils/portalQueryCache';
import { revealContainerVariants, revealItemVariants } from '../utils/motionReveal';

const PROFILE_CACHE_URL = '/api/profile';
const USERNAME_PATTERN = /^(?=.*[A-Za-z])[A-Za-z0-9_-]{5,64}$/;

function isValidUsername(value) {
  return USERNAME_PATTERN.test(String(value || '').trim());
}

function formatMfaTypeLabel(type) {
  if (!type) return 'Not configured';
  const t = String(type).toLowerCase();
  if (t === 'authenticator_app' || t === 'totp') return 'Authenticator app';
  if (t === 'email') return 'Email codes';
  if (t === 'sms') return 'SMS';
  return String(type).replace(/_/g, ' ');
}

export function ProfilePage() {
  const { user, refresh, provider, startProviderLogin } = useAuth();
  const cachedProfile = getCachedJson(PROFILE_CACHE_URL);
  const initialProfile = useMemo(() => {
    if (cachedProfile) return cachedProfile;
    if (user) {
      return {
        email: user.email || '',
        displayName: user.displayName || '',
        role: user.role || '',
        avatarUrl: user.avatarUrl || null,
        stores: [],
        customerAccounts: [],
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [profile, setProfile] = useState(initialProfile);
  const [displayName, setDisplayName] = useState(initialProfile?.displayName || '');
  const [email, setEmail] = useState(initialProfile?.email || '');
  const [username, setUsername] = useState(initialProfile?.username || '');
  const [canEditUsername, setCanEditUsername] = useState(Boolean(initialProfile?.canEditUsername));
  const [usernameModalOpen, setUsernameModalOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameIdentities, setUsernameIdentities] = useState(null);
  const [identityDeleting, setIdentityDeleting] = useState(null);
  const [error, setError] = useState('');
  const [emailNotice, setEmailNotice] = useState('');
  const [needsEmailReauth, setNeedsEmailReauth] = useState(false);
  const [toast, setToast] = useState('');
  const [infoToast, setInfoToast] = useState('');
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [mfa, setMfa] = useState(null);
  const [mfaError, setMfaError] = useState('');
  const [mfaLoaded, setMfaLoaded] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarZoom, setAvatarZoom] = useState(1.15);
  const [avatarError, setAvatarError] = useState('');
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [showAllStores, setShowAllStores] = useState(false);
  const [showAllCustomerAccounts, setShowAllCustomerAccounts] = useState(false);
  const avatarImgRef = useRef(null);
  const avatarCanvasRef = useRef(null);
  const avatarFileInputId = 'avatarFileInput';
  const avatarDragRef = useRef({ active: false, lastX: 0, lastY: 0 });
  const avatarPanRef = useRef({ x: 0, y: 0 });
  const avatarZoomRef = useRef(1.15);
  const avatarDrawRafRef = useRef(0);
  const showSocialButtons = Boolean(provider?.kinde?.showSocialButtons);
  const googleConnectionId = (provider?.kinde?.googleConnectionId || '').trim();
  const appleConnectionId = (provider?.kinde?.appleConnectionId || '').trim();

  const loadMfa = useCallback(async () => {
    if (!provider?.kinde?.enabled) return;
    setMfaError('');
    try {
      const response = await fetch('/api/profile/mfa', { credentials: 'include' });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || 'Failed to load MFA settings.');
      }
      const data = await response.json();
      setMfa(data?.mfa || null);
    } catch (e) {
      setMfa(null);
      setMfaError(e.message || 'Failed to load MFA settings.');
    } finally {
      setMfaLoaded(true);
    }
  }, [provider?.kinde?.enabled]);

  const mfaIsActive = useMemo(
    () => Boolean(mfa && (mfa.type || mfa.id || mfa.isVerified === true)),
    [mfa]
  );


  useEffect(() => {
    if (!toast) return;
    const handle = window.setTimeout(() => setToast(''), 4200);
    return () => window.clearTimeout(handle);
  }, [toast]);

  useEffect(() => {
    if (!infoToast) return;
    const handle = window.setTimeout(() => setInfoToast(''), 6000);
    return () => window.clearTimeout(handle);
  }, [infoToast]);

  useEffect(() => {
    const pending = sessionStorage.getItem('pending_social_link') || '';
    if (pending) {
      sessionStorage.removeItem('pending_social_link');
      setToast(`${pending === 'apple' ? 'Apple' : 'Google'} sign-in completed. If your verified email matches, it will be linked to your HK ID account.`);
    }

    // MFA flow: if we came back with a flow marker, refresh MFA status.
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('mfaFlow') === '1') {
        url.searchParams.delete('mfaFlow');
        window.history.replaceState({}, '', url.toString());
        sessionStorage.setItem('hk_mfa_flow_return', '1');
      }
    } catch {
      // ignore URL parsing issues
    }

    const profileAction = sessionStorage.getItem('hk_kinde_profile_action') || '';
    if (profileAction) {
      sessionStorage.removeItem('hk_kinde_profile_action');
      const actionMessages = {
        email: 'Email change completed. Verify your new address — the update will take effect after your next sign-in.',
        password: 'Password change completed. Your new password is now active.',
        mfa: 'MFA setup completed. Your two-factor authentication settings are updated.',
      };
      setInfoToast(actionMessages[profileAction] || 'Profile update completed.');
    }

    const reauthAction = sessionStorage.getItem('hk_profile_reauth_action') || '';
    if (reauthAction) {
      sessionStorage.removeItem('hk_profile_reauth_action');
      if (reauthAction === 'email') {
        setNewEmail('');
        setEmailModalOpen(true);
        setInfoToast('Verified. You can now change your email.');
      } else if (reauthAction === 'password') {
        setPasswordError('');
        setPasswordMessage('');
        setPasswordOpen(true);
        setInfoToast('Verified. You can now change your password.');
      }
    }

    const mfaReturn = sessionStorage.getItem('hk_mfa_flow_return') || '';
    if (mfaReturn) {
      sessionStorage.removeItem('hk_mfa_flow_return');
      loadMfa();
    }
  }, [loadMfa]);

  const connectSocial = useCallback(
    (kind) => {
      if (!provider?.kinde?.enabled) return;
      const connectionId = kind === 'apple' ? appleConnectionId : googleConnectionId;
      if (!connectionId) {
        setError(`${kind === 'apple' ? 'Apple' : 'Google'} connection is not configured.`);
        return;
      }

      sessionStorage.setItem('pending_social_link', kind);
      startProviderLogin('/profile', {
        connectionId,
        prompt: 'login',
        maxAge: 0,
        loginHint: email || undefined,
      });
    },
    [provider?.kinde?.enabled, appleConnectionId, googleConnectionId, startProviderLogin, email]
  );

  useEffect(() => {
    let isCancelled = false;
    const controller = new AbortController();
    (async () => {
      try {
        const data = await fetchJsonCached(PROFILE_CACHE_URL, { ttlMs: 30_000, signal: controller.signal });
        if (isCancelled) return;
        setProfile(data);
        setDisplayName(data.displayName || '');
        setEmail(data.email || '');
        setUsername(data.username || '');
        setCanEditUsername(Boolean(data.canEditUsername));
        setNewDisplayName(data.displayName || '');
        if (data.canEditUsername) {
          try {
            const idRes = await fetch('/api/profile/username-identities', { credentials: 'include', signal: controller.signal });
            if (!isCancelled && idRes.ok) {
              const idData = await idRes.json();
              setUsernameIdentities(idData.identities || []);
            }
          } catch {
            // non-critical
          }
        }
      } catch {
        if (!isCancelled) setError('Failed to load profile.');
      }
    })();
    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (profile) {
      setCachedJson(PROFILE_CACHE_URL, profile);
    }
  }, [profile]);

  useEffect(() => {
    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  const drawAvatarPreview = (canvas, img, zoom, pan) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const targetW = canvas.width;
    const targetH = canvas.height;

    ctx.clearRect(0, 0, targetW, targetH);

    // cover the square and then apply zoom
    const baseScale = Math.max(targetW / img.width, targetH / img.height);
    const scale = baseScale * zoom;
    const drawW = img.width * scale;
    const drawH = img.height * scale;
    const baseDx = (targetW - drawW) / 2;
    const baseDy = (targetH - drawH) / 2;

    // clamp pan so the image always covers the canvas (no empty space)
    const minDx = targetW - drawW;
    const maxDx = 0;
    const minDy = targetH - drawH;
    const maxDy = 0;

    const dx = Math.min(maxDx, Math.max(minDx, baseDx + (pan?.x || 0)));
    const dy = Math.min(maxDy, Math.max(minDy, baseDy + (pan?.y || 0)));

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, dx, dy, drawW, drawH);
  };

  const clampAvatarPan = (canvas, img, zoom, pan) => {
    const targetW = canvas.width;
    const targetH = canvas.height;

    const baseScale = Math.max(targetW / img.width, targetH / img.height);
    const scale = baseScale * zoom;
    const drawW = img.width * scale;
    const drawH = img.height * scale;
    const baseDx = (targetW - drawW) / 2;
    const baseDy = (targetH - drawH) / 2;

    const minDx = targetW - drawW;
    const maxDx = 0;
    const minDy = targetH - drawH;
    const maxDy = 0;

    const minPanX = minDx - baseDx;
    const maxPanX = maxDx - baseDx;
    const minPanY = minDy - baseDy;
    const maxPanY = maxDy - baseDy;

    const nextX = Math.min(maxPanX, Math.max(minPanX, pan.x || 0));
    const nextY = Math.min(maxPanY, Math.max(minPanY, pan.y || 0));

    return { x: nextX, y: nextY };
  };

  const scheduleAvatarDraw = useCallback(() => {
    if (avatarDrawRafRef.current) return;
    avatarDrawRafRef.current = window.requestAnimationFrame(() => {
      avatarDrawRafRef.current = 0;
      const canvas = avatarCanvasRef.current;
      const img = avatarImgRef.current;
      if (!canvas || !img) return;
      avatarPanRef.current = clampAvatarPan(canvas, img, avatarZoomRef.current, avatarPanRef.current);
      drawAvatarPreview(canvas, img, avatarZoomRef.current, avatarPanRef.current);
    });
  }, []);

  useEffect(() => {
    if (!avatarPreview) return;
    const img = new Image();
    img.onload = () => {
      avatarImgRef.current = img;
      const canvas = avatarCanvasRef.current;
      if (!canvas) return;
      canvas.width = 512;
      canvas.height = 512;
      avatarZoomRef.current = avatarZoom;
      avatarPanRef.current = { x: 0, y: 0 };
      drawAvatarPreview(canvas, img, avatarZoomRef.current, avatarPanRef.current);
    };
    img.src = avatarPreview;
  }, [avatarPreview, avatarZoom]);

  useEffect(() => {
    avatarZoomRef.current = avatarZoom;
    scheduleAvatarDraw();
  }, [avatarZoom, scheduleAvatarDraw]);

  useEffect(() => {
    if (!provider?.kinde?.enabled) {
      setMfa(null);
      setMfaError('');
      setMfaLoaded(false);
      return;
    }
    setMfaLoaded(false);
    loadMfa();
  }, [provider?.kinde?.enabled, loadMfa]);

  useEffect(() => {
    if (!provider?.kinde?.enabled) return undefined;
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        loadMfa();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [provider?.kinde?.enabled, loadMfa]);

  useEffect(() => {
    if (!avatarModalOpen) return;
    setNameModalOpen(false);
    setEmailModalOpen(false);
    setPasswordOpen(false);
  }, [avatarModalOpen]);

  useEffect(() => {
    if (!nameModalOpen) return;
    setAvatarModalOpen(false);
    setEmailModalOpen(false);
    setPasswordOpen(false);
  }, [nameModalOpen]);

  useEffect(() => {
    if (!emailModalOpen) return;
    setAvatarModalOpen(false);
    setNameModalOpen(false);
    setPasswordOpen(false);
  }, [emailModalOpen]);

  useEffect(() => {
    if (!passwordOpen) return;
    setAvatarModalOpen(false);
    setNameModalOpen(false);
    setEmailModalOpen(false);
  }, [passwordOpen]);

  const saveProfile = async (nextEmail, nextDisplayName) => {
    setError('');
    setEmailNotice('');
    setNeedsEmailReauth(false);
    const previousEmailNormalized = String(email || '').trim().toLowerCase();
    const nextEmailNormalized = String(nextEmail || '').trim().toLowerCase();
    const emailChanged = Boolean(nextEmailNormalized) && previousEmailNormalized !== nextEmailNormalized;

    const response = await fetch('/api/profile', {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: nextEmail, displayName: nextDisplayName }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.message || 'Failed to save profile.');
    }

    const data = await response.json();
    setProfile((prev) => ({ ...prev, ...data }));
    setEmail(data.email || '');
    setDisplayName(data.displayName || '');
    setNewDisplayName(data.displayName || '');
    setEmailNotice(data.emailVerificationNotice || '');
    setNeedsEmailReauth(Boolean(data.requiresReauthForEmailVerification));
    await refresh();
    setToast('Profile updated.');

    if (provider?.kinde?.enabled && emailChanged) {
      setInfoToast('Email updated. Please sign in again to verify your new email.');
      const returnTo = encodeURIComponent('/login?authState=email-changed&returnUrl=/profile');
      window.location.assign(`/api/auth/kinde/logout?returnUrl=${returnTo}`);
    }
  };

  const saveUsername = async (nextUsername) => {
    setError('');
    const normalized = String(nextUsername || '').trim();
    if (!isValidUsername(normalized)) {
      throw new Error('Username must be 5-64 chars, include at least one letter, and contain only letters, numbers, "-" or "_".');
    }

    const response = await fetch('/api/profile/username', {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: normalized }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.message || 'Failed to save username.');
    }

    const data = await response.json();
    setUsername(data.username || normalized);
    setCanEditUsername(true);
    setProfile((prev) => ({
      ...prev,
      username: data.username || normalized,
      canEditUsername: true,
    }));
    setCachedJson(PROFILE_CACHE_URL, {
      ...getCachedJson(PROFILE_CACHE_URL),
      username: data.username || normalized,
      canEditUsername: true,
    });
    await refresh();
    setToast('Username saved.');
    loadUsernameIdentities();
  };

  const loadUsernameIdentities = async () => {
    if (!canEditUsername) return;
    try {
      const res = await fetch('/api/profile/username-identities', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setUsernameIdentities(data.identities || []);
    } catch {
      // non-critical
    }
  };

  const deleteUsernameIdentity = async (identityId) => {
    setIdentityDeleting(identityId);
    try {
      const res = await fetch(`/api/profile/username-identities/${encodeURIComponent(identityId)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        setError(payload?.message || 'Failed to delete identity.');
        return;
      }
      setUsernameIdentities((prev) => (prev || []).filter((i) => i.id !== identityId));
      setToast('Old username removed.');
    } catch {
      setError('Failed to delete identity.');
    } finally {
      setIdentityDeleting(null);
    }
  };

  const hasUsername = Boolean((username || '').trim());

  const beginMfaEnrollmentChoice = useCallback(
    async () => {
      if (!provider?.kinde?.enabled) return;
      setMfaError('');
      try {
        // Reset enrollment so the identity provider shows the MFA setup wizard.
        const response = await fetch('/api/profile/mfa/change', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.message || 'Failed to start MFA setup.');
        }
      } catch (e) {
        setMfaError(e.message || 'Failed to start MFA setup.');
        return;
      }

      sessionStorage.setItem('hk_kinde_profile_action', 'mfa');
      startProviderLogin('/profile?mfaFlow=1', {
        loginHint: email || undefined,
        prompt: 'login',
        maxAge: 0,
      });
    },
    [provider?.kinde?.enabled, startProviderLogin, email]
  );

  const onPasswordChange = async () => {
    setPasswordError('');
    setPasswordMessage('');
    if (!newPassword || newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    if (!/^[\x20-\x7E]+$/.test(newPassword)) {
      setPasswordError('Password must use Latin/ASCII characters only.');
      return;
    }
    const hasUpper = /[A-Z]/.test(newPassword);
    const hasLower = /[a-z]/.test(newPassword);
    const hasDigit = /\d/.test(newPassword);
    const hasSymbol = /[^A-Za-z0-9]/.test(newPassword);
    if (!hasUpper || !hasLower || !hasDigit || !hasSymbol) {
      setPasswordError('Password must include uppercase, lowercase, number, and symbol.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    setIsSavingPassword(true);
    try {
      const response = await fetch('/api/profile/password', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword, confirmPassword }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || 'Failed to update password.');
      }

      setPasswordOpen(false);
      setNewPassword('');
      setConfirmPassword('');
      setToast('Password updated.');
      setPasswordMessage('Password updated successfully.');
    } catch (submissionError) {
      setPasswordError(submissionError.message || 'Failed to update password.');
    } finally {
      setIsSavingPassword(false);
    }
  };

  if (!profile && error) {
    return <p className="error-text">{error}</p>;
  }

  return (
    <section className="profile-page-grid">
      <motion.section
        className="profile-card"
        variants={revealContainerVariants}
        initial="hidden"
        animate="show"
      >
      <FloatingNotices
        success={toast}
        error={error}
        info={infoToast}
        onDismissSuccess={() => setToast('')}
        onDismissError={() => setError('')}
        onDismissInfo={() => setInfoToast('')}
      />
      <motion.div className="profile-header" variants={revealItemVariants}>
        <button
          type="button"
          className="profile-avatar-button"
          onClick={() => {
            setAvatarError('');
            setAvatarFile(null);
            if (avatarPreview) {
              URL.revokeObjectURL(avatarPreview);
              setAvatarPreview('');
            }
            setAvatarModalOpen(true);
          }}
          aria-label="Change profile photo"
          title="Change profile photo"
        >
          {profile?.avatarUrl ? (
            <img className="profile-avatar-image" src={profile.avatarUrl} alt="Avatar" />
          ) : (
            <div className="profile-avatar">{(profile?.displayName ?? 'HK').slice(0, 2).toUpperCase()}</div>
          )}
          <span className="profile-avatar-camera" aria-hidden="true">
            <Camera size={14} strokeWidth={2.25} />
          </span>
        </button>
        <div>
          <h2>Profile</h2>
          <p className="hint-text">Update your profile identity used across tools.</p>
        </div>
      </motion.div>

      <motion.div className="profile-layout-grid" variants={revealItemVariants}>
        <div className="profile-form">
          <label htmlFor="profileEmail">Email</label>
          <div className="field-action">
            <input id="profileEmail" className="field-action-input" type="email" value={email} readOnly />
            <button
              type="button"
              className="field-action-button"
              onClick={() => {
                if (provider?.kinde?.enabled) {
                  sessionStorage.setItem('hk_profile_reauth_action', 'email');
                  startProviderLogin('/profile', { prompt: 'login', maxAge: 0, loginHint: email || undefined });
                  return;
                }
                setNewEmail('');
                setEmailModalOpen(true);
              }}
            >
              Change
            </button>
          </div>

          <label htmlFor="profileRole">Role</label>
          <input id="profileRole" value={profile?.role ?? ''} readOnly />

          <label htmlFor="profileUsername">Username</label>
          <div className="field-action">
            <input
              id="profileUsername"
              className="field-action-input"
              value={username || (canEditUsername ? 'Not set' : '—')}
              readOnly
            />
            {canEditUsername ? (
              <button
                type="button"
                className="field-action-button"
                onClick={() => {
                  setNewUsername(hasUsername ? username : '');
                  setUsernameModalOpen(true);
                }}
              >
                {hasUsername ? 'Change' : 'Set'}
              </button>
            ) : null}
          </div>
          {canEditUsername ? (
            <p className="hint-text">Choose a unique username for sign-in (at least one letter). It cannot match a customer account number or login.</p>
          ) : null}

          <label htmlFor="profileDisplayName">Display Name</label>
          <div className="field-action">
            <input id="profileDisplayName" className="field-action-input" value={displayName} readOnly />
            <button
              type="button"
              className="field-action-button"
              onClick={() => {
                setNewDisplayName(displayName);
                setNameModalOpen(true);
              }}
            >
              Change
            </button>
          </div>

        <label htmlFor="profilePassword">Password</label>
        <div className="field-action">
          <input id="profilePassword" className="field-action-input" value="••••••••••••" readOnly />
        <button
          type="button"
          className="field-action-button"
          onClick={() => {
            if (provider?.kinde?.enabled) {
              sessionStorage.setItem('hk_profile_reauth_action', 'password');
              startProviderLogin('/profile', { prompt: 'login', maxAge: 0, loginHint: email || undefined, stepUp: 'password' });
              return;
            }
            setPasswordError('');
            setPasswordMessage('');
            setPasswordOpen(true);
          }}
        >
          Change
        </button>
        </div>

          {provider?.kinde?.enabled && (
            <>
            {mfaLoaded && !mfaError && !mfaIsActive ? (
              <div className="profile-mfa-promo" role="region" aria-label="Optional multi-factor authentication">
                <div className="profile-mfa-promo-inner">
                  <div className="profile-mfa-promo-copy">
                    <span className="profile-mfa-promo-badge">Recommended</span>
                    <h3 className="profile-mfa-promo-title">Add a second step to your sign-in</h3>
                    <p className="profile-mfa-promo-text">
                      MFA is optional for your account, but turning it on protects you if your password is ever compromised.
                      Use <strong>Set up</strong> below to choose a method (Authenticator app, Email codes, or SMS).
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            <label htmlFor="profileMfa">Multi-factor authentication</label>
            <div className="field-action">
              <input
                id="profileMfa"
                className="field-action-input"
                value={
                  mfaError
                    ? 'Unavailable'
                    : mfaIsActive
                      ? `Enabled (${formatMfaTypeLabel(mfa.type)})`
                      : 'Not configured'
                }
                readOnly
              />
              <button
                type="button"
                className="field-action-button"
                onClick={() => {
                  beginMfaEnrollmentChoice();
                }}
              >
                {mfaIsActive ? 'Change' : 'Set up'}
              </button>
            </div>

            {showSocialButtons && (
              <>
                <label>Connected accounts</label>
                <div className="connected-accounts-row">
                  {googleConnectionId ? (
                    <button type="button" className="ghost-button connected-account-button" onClick={() => connectSocial('google')}>
                      Connect Google
                    </button>
                  ) : (
                    <span className="hint-text">Google not configured</span>
                  )}
                  {appleConnectionId ? (
                    <button type="button" className="ghost-button connected-account-button" onClick={() => connectSocial('apple')}>
                      Connect Apple
                    </button>
                  ) : (
                    <span className="hint-text">Apple not configured</span>
                  )}
                </div>
              </>
            )}
            </>
          )}
        </div>

      </motion.div>

      {emailNotice && (
        <motion.div className="status-banner info" variants={revealItemVariants}>
          <strong>Email verification</strong>
          <span>{emailNotice}</span>
          {provider?.kinde?.enabled && needsEmailReauth ? (
            <span className="hint-text">Redirecting to finish verification and MFA setup...</span>
          ) : null}
        </motion.div>
      )}

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
        {avatarModalOpen && (
          <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div
              className="modal-card"
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3>Change avatar</h3>
              <p className="hint-text">Upload a PNG, JPEG, or WebP image (max 5MB). Then adjust scale and upload.</p>
              <div className="profile-form">
                <input
                  id={avatarFileInputId}
                  className="file-picker-hidden"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => {
                    setAvatarError('');
                    const file = event.target.files?.[0] || null;
                    setAvatarFile(file);
                    setAvatarZoom(1.15);
                    avatarZoomRef.current = 1.15;
                    avatarPanRef.current = { x: 0, y: 0 };
                    avatarImgRef.current = null;
                    if (avatarPreview) {
                      URL.revokeObjectURL(avatarPreview);
                      setAvatarPreview('');
                    }
                    if (file) {
                      setAvatarPreview(URL.createObjectURL(file));
                    }
                  }}
                />
                <div className="file-picker-row">
                  <button type="button" className="file-picker-button" onClick={() => document.getElementById(avatarFileInputId)?.click()}>
                    Choose photo
                  </button>
                  <span className="file-name">{avatarFile?.name || 'No file selected'}</span>
                </div>

                {avatarPreview && (
                  <>
                    <div className="avatar-crop-wrap">
                      <canvas
                        ref={avatarCanvasRef}
                        className="avatar-crop-canvas"
                        width={512}
                        height={512}
                        onPointerDown={(event) => {
                          const canvas = avatarCanvasRef.current;
                          const img = avatarImgRef.current;
                          if (!canvas || !img) return;
                          avatarDragRef.current.active = true;
                          avatarDragRef.current.lastX = event.clientX;
                          avatarDragRef.current.lastY = event.clientY;
                          canvas.setPointerCapture(event.pointerId);
                        }}
                        onPointerMove={(event) => {
                          const drag = avatarDragRef.current;
                          if (!drag.active) return;
                          const canvas = avatarCanvasRef.current;
                          if (!canvas) return;
                          const rect = canvas.getBoundingClientRect();
                          const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
                          const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
                          const dx = (event.clientX - drag.lastX) * scaleX;
                          const dy = (event.clientY - drag.lastY) * scaleY;
                          drag.lastX = event.clientX;
                          drag.lastY = event.clientY;
                          avatarPanRef.current = { x: avatarPanRef.current.x + dx, y: avatarPanRef.current.y + dy };
                          scheduleAvatarDraw();
                        }}
                        onPointerUp={(event) => {
                          const canvas = avatarCanvasRef.current;
                          if (canvas && canvas.hasPointerCapture(event.pointerId)) {
                            canvas.releasePointerCapture(event.pointerId);
                          }
                          avatarDragRef.current.active = false;
                        }}
                        onPointerCancel={(event) => {
                          const canvas = avatarCanvasRef.current;
                          if (canvas && canvas.hasPointerCapture(event.pointerId)) {
                            canvas.releasePointerCapture(event.pointerId);
                          }
                          avatarDragRef.current.active = false;
                        }}
                      />
                    </div>
                    <div className="avatar-zoom-row">
                      <label htmlFor="avatarZoomRange">Scale</label>
                      <input
                        id="avatarZoomRange"
                        type="range"
                        min="1"
                        max="2.4"
                        step="0.01"
                        value={avatarZoom}
                        onChange={(event) => setAvatarZoom(Number(event.target.value))}
                      />
                    </div>
                  </>
                )}
              </div>
              {avatarError && <p className="error-text">{avatarError}</p>}
              <div className="inline-row">
                <button
                  type="button"
                  className="ghost-button admin-inline-button"
                  onClick={() => {
                    setAvatarModalOpen(false);
                    setAvatarError('');
                    setAvatarFile(null);
                    avatarImgRef.current = null;
                    if (avatarPreview) {
                      URL.revokeObjectURL(avatarPreview);
                      setAvatarPreview('');
                    }
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary-button admin-inline-button"
                  disabled={!avatarFile || isUploadingAvatar}
                  onClick={async () => {
                    if (!avatarFile) return;
                    setIsUploadingAvatar(true);
                    setAvatarError('');
                    try {
                      const canvas = avatarCanvasRef.current;
                      if (!canvas) {
                        throw new Error('Please choose a photo first.');
                      }

                      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
                      if (!blob) {
                        throw new Error('Failed to process image.');
                      }

                      const form = new FormData();
                      form.append('file', blob, 'avatar.jpg');
                      const response = await fetch('/api/profile/avatar', { method: 'POST', credentials: 'include', body: form });
                      if (!response.ok) {
                        const payload = await response.json().catch(() => null);
                        throw new Error(payload?.message || 'Failed to upload avatar.');
                      }
                      const data = await response.json();
                      setProfile((current) => (current ? { ...current, avatarUrl: data.avatarUrl } : current));
                      setToast('Avatar updated.');
                      setAvatarModalOpen(false);
                    } catch (e) {
                      setAvatarError(e.message || 'Failed to upload avatar.');
                    } finally {
                      setIsUploadingAvatar(false);
                    }
                  }}
                >
                  {isUploadingAvatar ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        {usernameModalOpen && (
          <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div
              className="modal-card"
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3>{hasUsername ? 'Change username' : 'Set username'}</h3>
              <p className="hint-text">Choose a unique username for sign-in (at least one letter). It cannot match a customer account number or login.</p>
              <div className="profile-form">
                <label htmlFor="newUsernameInput">Username</label>
                <input
                  id="newUsernameInput"
                  value={newUsername}
                  onChange={(event) => setNewUsername(event.target.value)}
                  placeholder="your_username"
                  autoComplete="username"
                />
              </div>
              <div className="inline-row">
                <button type="button" className="ghost-button admin-inline-button" onClick={() => setUsernameModalOpen(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary-button admin-inline-button"
                  disabled={!isValidUsername(newUsername)}
                  onClick={async () => {
                    try {
                      await saveUsername(newUsername);
                      setUsernameModalOpen(false);
                    } catch (e) {
                      setError(e.message || 'Failed to save username.');
                    }
                  }}
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        {nameModalOpen && (
          <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div
              className="modal-card"
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3>Change display name</h3>
              <p className="hint-text">Update how your name appears across tools.</p>
              <div className="profile-form">
                <label htmlFor="newDisplayNameInput">Display name</label>
                <input
                  id="newDisplayNameInput"
                  value={newDisplayName}
                  onChange={(event) => setNewDisplayName(event.target.value)}
                  placeholder="Your name"
                />
              </div>
              <div className="inline-row">
                <button type="button" className="ghost-button admin-inline-button" onClick={() => setNameModalOpen(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary-button admin-inline-button"
                  onClick={async () => {
                    try {
                      await saveProfile(email, newDisplayName);
                      setNameModalOpen(false);
                    } catch (e) {
                      setError(e.message || 'Failed to save profile.');
                    }
                  }}
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {emailModalOpen && (
          <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div
              className="modal-card"
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3>Change email</h3>
              <p className="hint-text">
                Enter your new email address. This will update your sign-in identity and refresh your portal session.
              </p>
              <div className="profile-form">
                <label htmlFor="newEmailInput">New email</label>
                <input
                  id="newEmailInput"
                  type="email"
                  value={newEmail}
                  onChange={(event) => setNewEmail(event.target.value)}
                  placeholder="new@email.com"
                />
              </div>
              <div className="inline-row">
                <button
                  type="button"
                  className="ghost-button admin-inline-button"
                  onClick={() => {
                    setEmailModalOpen(false);
                    setNewEmail('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary-button admin-inline-button"
                  disabled={!newEmail.trim()}
                  onClick={() => {
                    const next = newEmail.trim().toLowerCase();
                    if (!next) return;
                    saveProfile(next, displayName)
                      .then(() => {
                        setEmailModalOpen(false);
                        setNewEmail('');
                      })
                      .catch((e) => setError(e.message || 'Failed to save profile.'));
                  }}
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {passwordOpen && (
          <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div
              className="modal-card"
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3>Change password</h3>
              <p className="hint-text">Set a new password for your account.</p>
              <div className="profile-form">
                <label htmlFor="newPasswordInput">New password</label>
                <input
                  id="newPasswordInput"
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  placeholder="Minimum 8 characters"
                />
                <label htmlFor="confirmPasswordInput">Confirm password</label>
                <input
                  id="confirmPasswordInput"
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="Repeat new password"
                />
              </div>
              {passwordMessage && <p className="success-text">{passwordMessage}</p>}
              {passwordError && <p className="error-text">{passwordError}</p>}
              <div className="inline-row">
                <button
                  type="button"
                  className="ghost-button admin-inline-button"
                  onClick={() => {
                    setPasswordOpen(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError('');
                    setPasswordMessage('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary-button admin-inline-button"
                  onClick={onPasswordChange}
                  disabled={isSavingPassword}
                >
                  {isSavingPassword ? 'Saving...' : 'Update password'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
          </AnimatePresence>,
          document.body
        )}
      </motion.section>

      <motion.aside
        className="profile-side-card profile-side-card-standalone"
        initial={{ opacity: 0, y: -18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.42, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
      >
        <h4>Stores</h4>
        <div className="user-pill-list compact">
          {(profile?.stores || []).length > 0 ? (
            (showAllStores ? profile.stores : profile.stores.slice(0, 5)).map((store) => (
              <span key={store.id} className="user-pill">
                {store.name}
              </span>
            ))
          ) : (
            <span className="hint-text">No stores assigned.</span>
          )}
        </div>
        {(profile?.stores || []).length > 5 && (
          <button
            type="button"
            className="profile-pill-expand"
            onClick={() => setShowAllStores((v) => !v)}
          >
            {showAllStores ? 'Show less' : `Show all (${profile.stores.length})`}
          </button>
        )}

        <h4>Customer accounts</h4>
        <div className="user-pill-list compact">
          {(profile?.customerAccounts || []).length > 0 ? (
            (showAllCustomerAccounts ? profile.customerAccounts : profile.customerAccounts.slice(0, 5)).map((customer) => (
              <span key={customer.id} className="user-pill">
                {customer.name}
              </span>
            ))
          ) : (
            <span className="hint-text">No customer accounts assigned.</span>
          )}
        </div>
        {(profile?.customerAccounts || []).length > 5 && (
          <button
            type="button"
            className="profile-pill-expand"
            onClick={() => setShowAllCustomerAccounts((v) => !v)}
          >
            {showAllCustomerAccounts ? 'Show less' : `Show all (${profile.customerAccounts.length})`}
          </button>
        )}
      </motion.aside>
    </section>
  );
}
